import { useEffect, useRef, type RefObject } from "react";

// Raised from 500ms to 2000ms so the 300→600 message bump fires AFTER
// the channel-switch burst has settled, not in the middle of it. 500ms
// was too short: the initial 50→300 hydrate + Zero ADD notification
// flush typically ran well past that window, and the preload piled a
// second MessageListView commit + 400-notification flush on top.
// 2000ms gives hydrate room to complete; preload becomes a quiet
// background bump rather than part of the visible switch cost.
const MESSAGE_WARM_PRELOAD_DELAY_MS = 2000;

interface UseWarmPreloadArgs {
  channelId: string;
  hasMoreMessages: boolean;
  hidden: boolean;
  messageLimit: number;
  messagePageSize: number;
  isNearBottomRef: RefObject<boolean>;
  onBumpLimit: () => void;
}

/**
 * Schedules a one-time message-limit bump 2 seconds after a channel
 * settles, so the next page is already loaded by the time the user
 * scrolls up. Skipped when:
 *   • The MessageArea is multiplexed and hidden (kept-alive sibling).
 *   • The channel has already been warmed.
 *   • The user has already scrolled away from the bottom.
 *   • The current message limit is already past the page size (i.e.
 *     a previous bump or a deep-link load already grew the window).
 */
export function useWarmPreload({
  channelId,
  hasMoreMessages,
  hidden,
  messageLimit,
  messagePageSize,
  isNearBottomRef,
  onBumpLimit,
}: UseWarmPreloadArgs) {
  const warmPreloadTimerRef = useRef<number | null>(null);
  const warmedChannelsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (hidden) return;
    if (!channelId || !hasMoreMessages) return;
    if (warmedChannelsRef.current.has(channelId)) return;
    if (!isNearBottomRef.current) return;

    if (messageLimit > messagePageSize) {
      warmedChannelsRef.current.add(channelId);
      return;
    }

    if (warmPreloadTimerRef.current !== null) {
      window.clearTimeout(warmPreloadTimerRef.current);
    }

    warmPreloadTimerRef.current = window.setTimeout(() => {
      if (!isNearBottomRef.current) return;
      // Defer the actual bump to the next idle slot so it doesn't
      // compete with any residual channel-switch work. Falls back to
      // synchronous invocation in environments without
      // requestIdleCallback.
      const fire = () => {
        if (!isNearBottomRef.current) return;
        onBumpLimit();
        warmedChannelsRef.current.add(channelId);
      };
      if (typeof window.requestIdleCallback === "function") {
        window.requestIdleCallback(fire, { timeout: 2000 });
      } else {
        fire();
      }
    }, MESSAGE_WARM_PRELOAD_DELAY_MS);

    return () => {
      if (warmPreloadTimerRef.current !== null) {
        window.clearTimeout(warmPreloadTimerRef.current);
        warmPreloadTimerRef.current = null;
      }
    };
  }, [
    channelId,
    hasMoreMessages,
    onBumpLimit,
    messageLimit,
    messagePageSize,
    hidden,
    isNearBottomRef,
  ]);
}
